import { useCallback } from 'react';
import { gql, MutationResult, useApolloClient, useMutation } from '@apollo/client';
import { SESSION_TIMED_OUT, useAuthToken } from './useAuthToken';
import { useHistory, useLocation } from 'react-router-dom';
import { useAnalytics, Events, useQueryParams } from '@hooks';
import { useLessonFeedbackStatusStorage } from './useLessonFeedbackStatusStorage';
import { getOrCreateSessionId, getAndClearSessionUserId, setSessionUserId } from '../utils/sentryTrackingConfig';
import { beginLogout, endLogout, getLogoutEventsSent, setLogoutEventsSent, resetLogoutTracking } from '../utils/logoutTrackingGuard';
import { logger } from '../utils/logger';

export const loginMutationGQL = gql`
  mutation login($input: LoginInput!) {
    login(input: $input) {
      token, uri, sid, verified, email,
      id
    }
  }
`;

export type LoginResponse = {
  token?: string;
  uri?: string;
  sid?: string;
  verified?: string;
  email?: string;
  id?: string;
};

type UseLoginMutationResponse = [(email: string, password: string) => void, () => void, MutationResult];
export const useLoginMutation = (isParentApp?: boolean): UseLoginMutationResponse => {
  const { setAuthToken, removeAuthToken, removeHsaAuthToken, removeLockAccount, removeAuthPassword, removeVisitProfile, removeParentAuthToken, removeFirstResetLesson } = useAuthToken();
  const { removeFeedbackStatus } = useLessonFeedbackStatusStorage();
  const client = useApolloClient();
  const { push } = useHistory();
  const params = useQueryParams();
  const returnUrl = params.get('returnUrl');
  const { search } = useLocation();

  const { identify, trackEvent, segment } = useAnalytics();

  const [mutation, mutationResults] = useMutation<{ login: LoginResponse }>(loginMutationGQL, {
    onCompleted: (data) => {
      if (data.login.token) {
        resetLogoutTracking(); // allow logout events on next logout
        removeAuthToken();
        removeFeedbackStatus();
        setAuthToken(data.login.token, true);
        
        const sessionTimedOut = localStorage.getItem(SESSION_TIMED_OUT);
        if (sessionTimedOut) {
          logger.debug('[Login] Clearing session timeout flag', {
            timeoutSetAt: new Date(Number(sessionTimedOut)).toISOString(),
            timeElapsed: Date.now() - Number(sessionTimedOut),
            timestamp: new Date().toISOString()
          });
        }
        localStorage.removeItem(SESSION_TIMED_OUT);
        if (data.login.id) {
          segment?.identify(data.login.id);
          identify(data.login.id, {
            email: data.login.email,
          });
        }
        const sessionId = getOrCreateSessionId();
        const timestamp = new Date().toISOString();
        const d = { email: data.login.email, loginType: 'Email', userId: data.login?.id, sessionId, timestamp };
        trackEvent(Events.actions.logIn, d);
        trackEvent(Events.actions.sessionStarted, d);
        if (data.login.id) setSessionUserId(data.login.id);
        if (returnUrl && search.replace('?returnUrl=', '').includes(returnUrl)) {          
          const sharePath = search.replace('?returnUrl=', '').replace(`${returnUrl}&`, `${returnUrl}?`)
          push(sharePath);  
        } else {
          push('/');
        }
      } else if (data.login.sid) {
        const state: any = { totpId: data.login.sid, email: data.login.email }
        if (data.login.verified !== 'true' && data.login.uri) {
          state.totpUri = data.login.uri
        }
        push('/verify', state);
      } else if (data.login.email) {
        push('/admin/verify', { email: data.login.email });
      }
    },
    onError: (error, options) => {
      trackEvent(Events.actions.logInFailed, {email: options?.variables?.email, loginType: 'Email', clientId: options?.variables?.clientId, message: "Login attempt failed", error: error.message});
      logger.debug(`[Error]`, error);
    },
  });

  const login = useCallback((email: string, password: string) => {
    return mutation({
      variables: {
        input: {
          email,
          password,
          clientId: localStorage.getItem('clientId')
        }
      },
    });
  }, [mutation]);

  const logout = useCallback(async () => {
    if (!beginLogout()) return;
    const withTimeout = async (p: Promise<unknown>, ms = 1200) => {
      await Promise.race([p, new Promise<void>((resolve) => setTimeout(resolve, ms))]);
    };

    // Only send logout events once per session (shared guard – useLoginMutation and useAuthToken.logout may both run)
    if (!getLogoutEventsSent()) {
      setLogoutEventsSent(true);
      const sessionId = getOrCreateSessionId();
      const userId = getAndClearSessionUserId();
      try {
        await withTimeout(trackEvent(Events.actions.sessionEnded, {
          userId: userId ?? undefined,
          sessionId,
          timestamp: new Date().toISOString(),
        }));
        await withTimeout(trackEvent(Events.actions.logOut));
        const segmentWithQueue = segment as { queue?: { flush?: () => Promise<unknown> } } | undefined;
        if (typeof segmentWithQueue?.queue?.flush === 'function') {
          await withTimeout(segmentWithQueue.queue.flush());
        } else {
          await new Promise((r) => setTimeout(r, 250));
        }
      } catch (e) {
        logger.error('[Login] Error tracking logout events:', e);
      }
    }

    try {
      await client.clearStore();
    } catch (e) {
      logger.error('[Login] clearStore failed during logout:', e);
    }
    removeAuthToken();
    removeHsaAuthToken();
    removeLockAccount();
    removeAuthPassword();
    removeVisitProfile();
    removeParentAuthToken();
    removeFirstResetLesson();
    endLogout();
    if (isParentApp) {
      window.location.href = 'https://openparachuteschools.com/';
    } else {
      window.location.href = '/login';
    }
  }, [client, isParentApp, removeAuthPassword, removeAuthToken, removeHsaAuthToken, removeLockAccount, removeVisitProfile, removeParentAuthToken, trackEvent, removeFirstResetLesson, segment]);

  return [login, logout, mutationResults];
}
